import { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import { LogOut, Camera, Trash2, BadgeCheck, ShieldAlert, Pencil, Eye, X, MapPin } from "lucide-react";
import { api, photoUrl } from "../lib/api";
import { detectLocation } from "../lib/geo";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/useLang";
import { getAppStrings, getSetup, PHYS_OPTIONS, LOCATION_PREFS } from "../i18n/appI18n";
import DailyQuestions from "./DailyQuestions";
import Matches from "./Matches";
import Verify from "./Verify";
import VerifyEmail from "./VerifyEmail";
import Onboarding from "./Onboarding";
import ProfileSetup from "./ProfileSetup";

const fieldClass =
  "w-full bg-black/40 border border-reveal-rose/40 rounded-xl px-4 py-3 font-body text-reveal-cream placeholder:text-reveal-cream/40 outline-none focus:border-reveal-rose transition-colors";

const GENDERS = ["Woman", "Man", "Non-binary", "Other"];
const LOOKING = ["Women", "Men", "Everyone"];
const AGES = Array.from({ length: 48 }, (_, i) => 18 + i); // 18..65

function ProfileForm({ initial, onSaved }) {
  const lang = useLang();
  const px = getAppStrings(lang).profileExtra;
  const s = getSetup(lang);
  const [form, setForm] = useState({
    display_name: initial?.display_name || "",
    age: initial?.age || "",
    gender: initial?.gender || "",
    looking_for: initial?.looking_for || "",
    city: initial?.city || "",
    country: initial?.country || "",
    country_name: initial?.country_name || "",
    latitude: initial?.latitude ?? null,
    longitude: initial?.longitude ?? null,
    location_pref: initial?.location_pref || "my_country",
    bio: initial?.bio || "",
    min_age: initial?.min_age || 18,
    max_age: initial?.max_age || 65,
  });
  const [physical, setPhysical] = useState(initial?.physical || {});
  const [prefs, setPrefs] = useState(initial?.physical_prefs || {});
  const [saving, setSaving] = useState(false);
  const [detecting, setDetecting] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const setAttr = (dim, key) => setPhysical((p) => ({ ...p, [dim]: p[dim] === key ? null : key }));
  const togglePref = (dim, key) => setPrefs((p) => {
    const cur = p[dim] || [];
    return { ...p, [dim]: cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key] };
  });

  const detect = async () => {
    setDetecting(true);
    try {
      const loc = await detectLocation();
      setForm((f) => ({ ...f, latitude: loc.latitude, longitude: loc.longitude, city: loc.city || f.city, country: loc.country, country_name: loc.country_name }));
    } catch {
      toast.message(s.steps.location.denied);
    } finally {
      setDetecting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        age: form.age ? parseInt(form.age, 10) : null,
        min_age: parseInt(form.min_age, 10),
        max_age: parseInt(form.max_age, 10),
        physical,
        physical_prefs: prefs,
      };
      const { data } = await api.put("/profile", payload);
      toast.success("Profile saved");
      // recompute future matches with any updated preferences (non-blocking)
      api.post("/matches/generate").catch(() => {});
      onSaved(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="profile-form">
      <div>
        <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">Display name</label>
        <input required value={form.display_name} onChange={set("display_name")} data-testid="profile-name-input" className={fieldClass} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">Age</label>
          <input type="number" min="18" max="120" required value={form.age} onChange={set("age")} data-testid="profile-age-input" className={fieldClass} />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">City</label>
          <input value={form.city} onChange={set("city")} data-testid="profile-city-input" className={fieldClass} />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">I am a</label>
          <select value={form.gender} onChange={set("gender")} data-testid="profile-gender-select" className={`${fieldClass} cursor-pointer`}>
            <option value="" className="text-black">Select</option>
            {GENDERS.map((g) => <option key={g} value={g} className="text-black">{g}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">Looking for</label>
          <select value={form.looking_for} onChange={set("looking_for")} data-testid="profile-lookingfor-select" className={`${fieldClass} cursor-pointer`}>
            <option value="" className="text-black">Select</option>
            {LOOKING.map((g) => <option key={g} value={g} className="text-black">{g}</option>)}
          </select>
        </div>
      </div>

      {/* Age range */}
      <div>
        <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">{px.ageRange}</label>
        <div className="flex items-center gap-3">
          <select value={form.min_age} onChange={set("min_age")} data-testid="profile-minage-select" className={`${fieldClass} cursor-pointer`}>
            {AGES.map((a) => <option key={a} value={a} className="text-black">{a === 65 ? px.plus : a}</option>)}
          </select>
          <span className="text-reveal-cream/50 text-sm">{px.to}</span>
          <select value={form.max_age} onChange={set("max_age")} data-testid="profile-maxage-select" className={`${fieldClass} cursor-pointer`}>
            {AGES.map((a) => <option key={a} value={a} className="text-black">{a === 65 ? px.plus : a}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">{s.steps.location.title}</label>
        <button type="button" onClick={detect} disabled={detecting} data-testid="profile-detect-location-button"
          className="w-full inline-flex items-center justify-center gap-2 border border-reveal-rose/40 text-reveal-cream py-2.5 rounded-full text-sm hover:bg-reveal-rose/10 transition-colors disabled:opacity-60 mb-3">
          <MapPin className="w-4 h-4" /> {detecting ? s.steps.location.detecting : s.steps.location.detect}
        </button>
        <input value={form.country_name} onChange={set("country_name")} placeholder={s.steps.location.country} data-testid="profile-country-input" className={fieldClass} />
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">{s.steps.locationPref.title}</label>
        <div className="space-y-2" data-testid="profile-locationpref">
          {LOCATION_PREFS.map((p) => {
            const opt = s.locationPrefOptions[p];
            const on = form.location_pref === p;
            return (
              <button type="button" key={p} onClick={() => setForm((f) => ({ ...f, location_pref: p }))} data-testid={`profile-locpref-${p}`}
                className={`w-full text-left px-4 py-2.5 rounded-xl border text-sm transition-colors ${on ? "bg-reveal-rose/15 border-reveal-rose text-reveal-cream" : "bg-black/20 border-reveal-rose/20 text-reveal-cream/70 hover:border-reveal-rose/50"}`}>
                {opt.label} <span className="text-reveal-cream/40 text-xs">· {opt.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-xs uppercase tracking-widest text-reveal-rose mb-2">About you</label>
        <textarea rows={4} value={form.bio} onChange={set("bio")} data-testid="profile-bio-input"
          placeholder="Share something worth discovering..." className={`${fieldClass} resize-none`} />
      </div>

      {/* Physical attraction */}
      <div className="pt-2 border-t border-reveal-rose/10">
        <h3 className="font-heading text-lg text-reveal-cream mt-3">{px.physicalTitle}</h3>
        <p className="text-xs text-reveal-cream/45 mb-4">{px.physicalSub}</p>

        <p className="text-xs uppercase tracking-widest text-reveal-rose mb-3">{px.attributes}</p>
        <div className="space-y-3 mb-5" data-testid="physical-attributes">
          {Object.keys(PHYS_OPTIONS).map((dim) => (
            <div key={dim}>
              <span className="text-xs text-reveal-cream/55 block mb-1.5">{px.dims[dim].label}</span>
              <div className="flex flex-wrap gap-2">
                {PHYS_OPTIONS[dim].map((key) => (
                  <button type="button" key={key} onClick={() => setAttr(dim, key)} data-testid={`attr-${dim}-${key}`}
                    className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                      physical[dim] === key ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:border-reveal-rose/50"
                    }`}>{px.dims[dim].options[key]}</button>
                ))}
              </div>
            </div>
          ))}
        </div>

        <p className="text-xs uppercase tracking-widest text-reveal-rose mb-3">{px.preferences}</p>
        <div className="space-y-3" data-testid="physical-preferences">
          {Object.keys(PHYS_OPTIONS).map((dim) => (
            <div key={dim}>
              <span className="text-xs text-reveal-cream/55 block mb-1.5">{px.dims[dim].label}</span>
              <div className="flex flex-wrap gap-2">
                {PHYS_OPTIONS[dim].map((key) => {
                  const on = (prefs[dim] || []).includes(key);
                  return (
                    <button type="button" key={key} onClick={() => togglePref(dim, key)} data-testid={`pref-${dim}-${key}`}
                      className={`px-3 py-1.5 rounded-full text-xs transition-colors ${
                        on ? "bg-reveal-rose/20 border border-reveal-rose text-reveal-cream" : "border border-reveal-rose/25 text-reveal-cream/70 hover:border-reveal-rose/50"
                      }`}>{px.dims[dim].options[key]}</button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button type="submit" disabled={saving} data-testid="profile-save-button"
        className="w-full bg-reveal-rose text-black font-medium py-3 rounded-full hover:bg-[#c9987b] transition-colors disabled:opacity-60">
        {saving ? "Saving..." : "Save Profile"}
      </button>
    </form>
  );
}

function Photos({ profile, onChange }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const photos = profile?.photos || [];

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo added");
      onChange(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (path) => {
    try {
      const { data } = await api.delete(`/profile/photo`, { params: { path } });
      onChange(data);
    } catch {
      toast.error("Could not remove photo");
    }
  };

  return (
    <div data-testid="profile-photos">
      <div className="grid grid-cols-3 gap-3">
        {photos.map((p) => (
          <div key={p.path} className="relative aspect-square rounded-xl overflow-hidden border border-reveal-rose/20 group">
            <img src={photoUrl(p.path)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(p.path)} data-testid="delete-photo-button"
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-reveal-cream/80 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:text-red-400">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        <button onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="add-photo-button"
          className="aspect-square rounded-xl border border-dashed border-reveal-rose/40 flex flex-col items-center justify-center gap-2 text-reveal-cream/50 hover:text-reveal-rose hover:border-reveal-rose/70 transition-colors">
          <Camera className="w-6 h-6" />
          <span className="text-xs">{uploading ? "Uploading..." : "Add photo"}</span>
        </button>
      </div>
      <input ref={inputRef} type="file" accept="image/*" onChange={upload} className="hidden" data-testid="photo-file-input" />
    </div>
  );
}

export default function AppDashboard() {
  const { user, logout } = useAuth();
  const lang = useLang();
  const px = getAppStrings(lang).profileExtra;
  const setupS = getSetup(lang);
  const [profile, setProfile] = useState(undefined); // undefined = loading
  const [editing, setEditing] = useState(false);
  const [tab, setTab] = useState("matches");
  const [preview, setPreview] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profile/me");
        setProfile(data);
      } catch {
        setProfile(null);
      }
    })();
  }, []);

  const onSaved = (data) => { setProfile(data); setEditing(false); };

  // Gate: email verification -> (new signups) profile setup -> onboarding, before the app.
  if (user && !user.email_verified) return <VerifyEmail />;
  if (user && !user.onboarding_completed) {
    if (profile === undefined) {
      return <div className="min-h-screen bg-reveal-black flex items-center justify-center text-reveal-cream/60 font-body">Loading…</div>;
    }
    if (!profile || !profile.completed) {
      return <ProfileSetup onComplete={setProfile} />;
    }
    return <Onboarding />;
  }

  const profileIncomplete = profile && !profile.completed;
  const goCompleteProfile = () => { setTab("profile"); setEditing(true); };

  return (
    <div className="min-h-screen bg-reveal-black text-reveal-cream font-body" data-testid="app-dashboard">
      <header className="border-b border-reveal-rose/10 px-5 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <span className="font-heading text-2xl text-reveal-rose">Reveal</span>
          <div className="flex items-center gap-3">
            <span className="text-sm text-reveal-cream/70 hidden sm:inline" data-testid="dashboard-username">{user?.name || user?.email}</span>
            <button onClick={logout} data-testid="logout-button"
              className="inline-flex items-center gap-2 border border-reveal-rose/40 text-reveal-cream px-4 py-2 rounded-full text-sm hover:bg-reveal-rose/10 transition-colors">
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 py-10">
        <div className="flex gap-2 mb-8" data-testid="dashboard-tabs">
          <button onClick={() => setTab("profile")} data-testid="tab-profile"
            className={`px-5 py-2 rounded-full text-sm font-body transition-colors ${
              tab === "profile" ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
            }`}>
            Profile
          </button>
          <button onClick={() => setTab("daily")} data-testid="tab-daily"
            className={`px-5 py-2 rounded-full text-sm font-body transition-colors ${
              tab === "daily" ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
            }`}>
            Daily Questions
          </button>
          <button onClick={() => setTab("matches")} data-testid="tab-matches"
            className={`px-5 py-2 rounded-full text-sm font-body transition-colors ${
              tab === "matches" ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
            }`}>
            Matches
          </button>
          <button onClick={() => setTab("verify")} data-testid="tab-verify"
            className={`px-5 py-2 rounded-full text-sm font-body transition-colors ${
              tab === "verify" ? "bg-reveal-rose text-black" : "border border-reveal-rose/25 text-reveal-cream/70 hover:bg-reveal-rose/10"
            }`}>
            Verify
          </button>
        </div>

        {tab === "daily" ? (
          <DailyQuestions />
        ) : tab === "matches" ? (
          <>
            {profileIncomplete && (
              <button onClick={goCompleteProfile} data-testid="complete-profile-banner"
                className="w-full mb-6 flex items-center justify-between gap-3 rounded-2xl border border-reveal-rose/30 bg-reveal-rose/10 px-5 py-4 text-left hover:bg-reveal-rose/15 transition-colors">
                <span className="text-sm text-reveal-cream">{setupS.banner}</span>
                <span className="text-sm text-reveal-rose font-medium whitespace-nowrap">{setupS.completeProfile} →</span>
              </button>
            )}
            <Matches />
          </>
        ) : tab === "verify" ? (
          <Verify />
        ) : (
          <>
            <div className="mb-8 flex items-start justify-between gap-4">
              <div>
                <h1 className="font-heading text-4xl text-reveal-cream mb-1">Your Profile</h1>
                <p className="text-sm text-reveal-cream/55">
                  {editing ? "Complete your profile to start connecting." : "This is how others will begin to know you."}
                </p>
              </div>
              {profile && profile.completed && !editing && (
                <button onClick={() => setPreview(true)} data-testid="preview-profile-button"
                  className="inline-flex items-center gap-2 border border-reveal-rose/40 text-reveal-cream px-4 py-2 rounded-full text-sm hover:bg-reveal-rose/10 transition-colors shrink-0">
                  <Eye className="w-4 h-4" /> {px.preview}
                </button>
              )}
            </div>

            {profile === undefined ? (
              <p className="text-reveal-cream/50">Loading...</p>
            ) : (
              <div className="grid md:grid-cols-2 gap-8">
                <section className="bg-reveal-burgundy/60 border border-reveal-rose/15 rounded-2xl p-6">
                  <h2 className="font-heading text-xl text-reveal-cream mb-4">Photos</h2>
                  <Photos profile={profile} onChange={setProfile} />
                  <p className="text-xs text-reveal-cream/40 mt-3">Photos unblur progressively as connections deepen.</p>
                </section>

                <section className="bg-reveal-burgundy/60 border border-reveal-rose/15 rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl text-reveal-cream">Details</h2>
                    {!editing && (
                      <button onClick={() => setEditing(true)} data-testid="edit-profile-button"
                        className="inline-flex items-center gap-1.5 text-sm text-reveal-rose hover:text-reveal-cream transition-colors">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    )}
                  </div>

                  {editing ? (
                    <ProfileForm initial={profile} onSaved={onSaved} />
                  ) : (
                    <div className="space-y-4" data-testid="profile-view">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-heading text-2xl text-reveal-cream">{profile.display_name}</span>
                          {profile.age && <span className="text-reveal-cream/60">· {profile.age}</span>}
                        </div>
                        {profile.city && <span className="text-sm text-reveal-cream/55">{profile.city}</span>}
                      </div>
                      <div className="flex flex-wrap gap-2 text-xs">
                        {profile.gender && <span className="px-3 py-1 rounded-full bg-reveal-rose/10 text-reveal-rose">{profile.gender}</span>}
                        {profile.looking_for && <span className="px-3 py-1 rounded-full bg-reveal-rose/10 text-reveal-rose">Looking for {profile.looking_for}</span>}
                      </div>
                      {profile.bio && <p className="text-sm text-reveal-cream/75 leading-relaxed">{profile.bio}</p>}
                      <div className="flex items-center gap-2 pt-2 text-sm" data-testid="verification-status">
                        {profile.verified ? (
                          <><BadgeCheck className="w-4 h-4 text-green-400" /><span className="text-green-400">Verified</span></>
                        ) : (
                          <><ShieldAlert className="w-4 h-4 text-reveal-cream/40" /><span className="text-reveal-cream/50">Not verified yet</span></>
                        )}
                      </div>
                    </div>
                  )}
                </section>
              </div>
            )}
          </>
        )}
      </main>

      {preview && profile && (
        <ProfilePreview profile={profile} px={px} onClose={() => setPreview(false)} />
      )}
    </div>
  );
}

function ProfilePreview({ profile, px, onClose }) {
  const photos = profile.photos || [];
  const primary = photos.find((p) => p.primary) || photos[0];
  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-5" onClick={onClose} data-testid="profile-preview-modal">
      <div className="w-full max-w-sm bg-reveal-burgundy border border-reveal-rose/25 rounded-3xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-reveal-rose/10">
          <span className="text-xs uppercase tracking-widest text-reveal-rose">{px.previewTitle}</span>
          <button onClick={onClose} data-testid="preview-close-button" className="text-reveal-cream/60 hover:text-reveal-cream transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="aspect-[4/5] bg-black/40 relative">
          {primary ? (
            <img src={photoUrl(primary.path)} alt="" className="w-full h-full object-cover" style={{ filter: "blur(18px)" }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-reveal-cream/30 text-sm">No photo yet</div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-reveal-burgundy via-transparent to-transparent" />
          <div className="absolute bottom-4 left-5 right-5">
            <div className="flex items-center gap-2">
              <span className="font-heading text-3xl text-reveal-cream">{profile.display_name}</span>
              {profile.age && <span className="text-reveal-cream/70 text-lg">{profile.age}</span>}
              {profile.verified && <BadgeCheck className="w-5 h-5 text-green-400" />}
            </div>
            {profile.city && <span className="text-sm text-reveal-cream/70">{profile.city}</span>}
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-[11px] text-reveal-cream/40 italic">Photos start blurred and unblur as your connection deepens.</p>
          {profile.bio && <p className="text-sm text-reveal-cream/80 leading-relaxed">{profile.bio}</p>}
          {(profile.min_age || profile.max_age) && (
            <p className="text-xs text-reveal-cream/50">
              {px.lookingForAges
                .replace("{min}", profile.min_age || 18)
                .replace("{max}", (profile.max_age || 65) >= 65 ? px.plus : profile.max_age)}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
